// src/pages/TerminosDeServicio.jsx
import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';

function formatDate(year, month, day) {
  const date = new Date(year, month - 1, day); // month - 1 porque los meses van de 0-11
  return date.toLocaleDateString('es-MX', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

const TerminosDeServicio = () => {
  const lastUpdated = formatDate(2025, 12, 15);
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col">
      <Header />
      <section
        className="mt-24 pt-32 pb-20 px-4 relative bg-cover bg-center bg-no-repeat bg-packsys-blue bg-blend-multiply"
        style={{
          backgroundImage: 'url(/tracking_terminos_condiciones.png)',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-blue-800/20 to-blue-500/30"></div>
        {/* Overlay para mejor legibilidad del texto */}
        <div className="absolute inset-0 bg-black/20"></div>

        <div className="max-w-6xl mx-auto text-center relative z-10">
          <h1 className="text-4xl md:text-6xl font-black text-white mb-6 animate-fade-in-up drop-shadow-lg">
            Términos de <span className="text-packsys-orange">Servicio</span>
          </h1>
          <div className="inline-flex items-center gap-2 bg-blue-50 px-4 py-2 rounded-full mt-4">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
            <span className="text-blue-600 font-medium">
              Última actualización: {lastUpdated}
            </span>
          </div>
        </div>
      </section>

      <main className="flex-1 pt-12 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Contenido Principal */}
          <div className="bg-white rounded-3xl shadow-2xl p-6 md:p-10 transform transition-all duration-700 hover:shadow-3xl">
            {/* Sección de Aceptación */}
            <section className="mb-12 animate-slide-up">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
                  <span className="text-white text-lg">✓</span>
                </div>
                1. Aceptación de los Términos
              </h2>
              <div className="bg-blue-50 rounded-2xl p-6 border border-blue-100">
                <p className="text-gray-700 leading-relaxed mb-4">
                  Al utilizar el sistema de Tracking de Pedidos de Packsys,
                  usted acepta estos Términos de Servicio. Si no está de acuerdo
                  con alguna parte de estos términos, por favor no utilice
                  nuestro servicio.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Estos términos constituyen un acuerdo legal entre usted y
                  Packsys S.A. de C.V. ("Packsys") respecto al uso de nuestra
                  plataforma de seguimiento de pedidos.
                </p>
              </div>
            </section>

            {/* Definiciones */}
            <section className="mb-12 animate-slide-up animation-delay-300">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <span className="text-white text-lg">📖</span>
                </div>
                2. Definiciones
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  {
                    term: 'Sistema de Tracking',
                    definition:
                      'La plataforma digital proporcionada por Packsys para el seguimiento en tiempo real de pedidos y envíos.',
                  },
                  {
                    term: 'Usuario Autorizado',
                    definition:
                      'Persona o entidad que ha sido debidamente registrada y tiene acceso autorizado al panel de administración del sistema.',
                  },
                  {
                    term: 'Datos de Tracking',
                    definition:
                      'Información relacionada con el estado, ubicación y detalles de los pedidos.',
                  },
                  {
                    term: 'Servicio',
                    definition:
                      'El sistema de seguimiento de pedidos proporcionado por Packsys a través de su plataforma digital.',
                  },
                ].map((item, index) => (
                  <div
                    key={index}
                    className="bg-gray-50 rounded-xl p-4 border border-gray-200 hover:border-purple-200 transition-colors duration-300"
                  >
                    <h3 className="font-bold text-purple-600 mb-2">
                      {item.term}
                    </h3>
                    <p className="text-gray-600 text-sm">{item.definition}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Uso del Servicio */}
            <section className="mb-12 animate-slide-up animation-delay-600">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-green-600 rounded-xl flex items-center justify-center">
                  <span className="text-white text-lg">🔐</span>
                </div>
                3. Uso del Servicio
              </h2>
              <div className="space-y-6">
                {[
                  {
                    title: 'Uso Aceptable',
                    content:
                      'El sistema debe ser utilizado únicamente para fines legítimos de seguimiento de pedidos. Queda prohibido cualquier uso fraudulento, malintencionado o que interfiera con el funcionamiento normal del servicio.',
                  },
                  {
                    title: 'Responsabilidades del Usuario',
                    content:
                      'Es responsabilidad del usuario notificar inmediatamente a Packsys sobre cualquier uso no autorizado de su cuenta o cualquier otra violación de seguridad.',
                  },
                ].map((item, index) => (
                  <div
                    key={index}
                    className="bg-green-50 rounded-xl p-5 border border-green-100 hover:shadow-md transition-shadow duration-300"
                  >
                    <h3 className="font-bold text-green-700 mb-3 text-lg">
                      {item.title}
                    </h3>
                    <p className="text-gray-700">{item.content}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Privacidad y Protección de Datos */}
            <section className="mb-12 animate-slide-up animation-delay-900">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-r from-cyan-500 to-cyan-600 rounded-xl flex items-center justify-center">
                  <span className="text-white text-lg">🔒</span>
                </div>
                4. Privacidad y Protección de Datos
              </h2>
              <div className="bg-cyan-50 rounded-2xl p-6 border border-cyan-100">
                <h3 className="font-bold text-cyan-700 text-xl mb-4">
                  Tratamiento de Datos Personales
                </h3>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  Packsys se compromete a proteger la privacidad de los datos
                  personales de sus usuarios. El tratamiento de datos personales
                  se realizará conforme a lo establecido en nuestra Política de
                  Privacidad y en cumplimiento de la Ley Federal de Protección
                  de Datos Personales en Posesión de los Particulares.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                  {[
                    'Recopilamos únicamente los datos necesarios para proporcionar el servicio de tracking',
                    'Los datos de tracking se almacenan de forma segura y se protegen con medidas de seguridad adecuadas',
                    'No compartimos información personal con terceros, debido a que la información de tracking es guardada localmente en el dispositivo del usuario',
                    'Los usuarios pueden ejercer sus derechos ARCO (Acceso, Rectificación, Cancelación y Oposición)',
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-cyan-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <span className="text-white text-xs">✓</span>
                      </div>
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Limitación de Responsabilidad */}
            <section className="mb-12 animate-slide-up animation-delay-1200">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-r from-amber-500 to-amber-600 rounded-xl flex items-center justify-center">
                  <span className="text-white text-lg">⚠️</span>
                </div>
                5. Limitación de Responsabilidad
              </h2>
              <div className="bg-amber-50 rounded-2xl p-6 border border-amber-100">
                <h3 className="font-bold text-amber-700 text-xl mb-4">
                  Alcance del Servicio
                </h3>
                <p className="text-gray-700 mb-4 leading-relaxed">
                  Packsys proporciona el sistema de tracking "tal cual" y "según
                  disponibilidad". Si bien nos esforzamos por mantener la
                  precisión y disponibilidad de la información, no podemos
                  garantizar que:
                </p>

                <ul className="space-y-3 mb-6">
                  {[
                    'La información de tracking esté siempre disponible y actualizada en tiempo real',
                    'El servicio esté libre de interrupciones o errores técnicos',
                    'La información proporcionada sea 100% precisa en todo momento',
                    'El sistema sea compatible con todos los dispositivos y navegadores',
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <span className="text-amber-500 font-bold">•</span>
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>

                <div className="bg-white rounded-xl p-4 border border-amber-200">
                  <p className="text-gray-700 text-sm italic">
                    En ningún caso Packsys será responsable por daños
                    indirectos, incidentales, especiales o consecuentes que
                    resulten del uso o la imposibilidad de uso del sistema de
                    tracking.
                  </p>
                </div>
              </div>
            </section>

            {/* Propiedad Intelectual */}
            <section className="mb-12 animate-slide-up animation-delay-1500">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-r from-red-500 to-red-600 rounded-xl flex items-center justify-center">
                  <span className="text-white text-lg">©</span>
                </div>
                6. Propiedad Intelectual
              </h2>
              <div className="bg-red-50 rounded-2xl p-6 border border-red-100">
                <p className="text-gray-700 mb-4 leading-relaxed">
                  Todos los derechos de propiedad intelectual sobre el sistema
                  de tracking, incluyendo pero no limitado a software, diseño,
                  interfaz de usuario, logotipos y contenido, son propiedad
                  exclusiva de Packsys o de sus licenciantes.
                </p>

                <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    {
                      icon: '💻',
                      title: 'Software',
                      description:
                        'El código fuente y la plataforma son propiedad de Packsys',
                    },
                    {
                      icon: '🎨',
                      title: 'Diseño',
                      description:
                        'Interfaz de usuario y experiencia de usuario protegidos',
                    },
                    {
                      icon: '📊',
                      title: 'Datos',
                      description:
                        'Estructura y presentación de datos son propiedad intelectual',
                    },
                  ].map((item, index) => (
                    <div
                      key={index}
                      className="bg-white rounded-xl p-4 text-center border border-red-200"
                    >
                      <div className="text-2xl mb-2">{item.icon}</div>
                      <h4 className="font-bold text-red-600 mb-1">
                        {item.title}
                      </h4>
                      <p className="text-gray-600 text-sm">
                        {item.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Modificaciones a los Términos */}
            <section className="mb-12 animate-slide-up animation-delay-1800">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-xl flex items-center justify-center">
                  <span className="text-white text-lg">🔄</span>
                </div>
                7. Modificaciones a los Términos
              </h2>
              <div className="bg-indigo-50 rounded-2xl p-6 border border-indigo-100">
                <p className="text-gray-700 mb-4 leading-relaxed">
                  Packsys se reserva el derecho de modificar estos Términos de
                  Servicio en cualquier momento. Las modificaciones entrarán en
                  vigor inmediatamente después de su publicación en esta página.
                </p>

                <div className="flex items-center gap-4 bg-white rounded-xl p-4 mt-4 border border-indigo-200">
                  <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-indigo-600 text-xl">📢</span>
                  </div>
                  <div>
                    <p className="text-gray-700 font-medium">
                      Es responsabilidad del usuario revisar periódicamente
                      estos términos. El uso continuado del servicio después de
                      cualquier modificación constituye la aceptación de los
                      nuevos términos.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Legislación Aplicable */}
            <section className="animate-fade-in animation-delay-2100">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-r from-gray-600 to-gray-700 rounded-xl flex items-center justify-center">
                  <span className="text-white text-lg">⚖️</span>
                </div>
                8. Legislación Aplicable y Jurisdicción
              </h2>
              <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-bold text-gray-700 text-lg mb-3">
                      Legislación
                    </h3>
                    <p className="text-gray-600">
                      Estos Términos de Servicio se regirán e interpretarán de
                      acuerdo con las leyes de los Estados Unidos Mexicanos, sin
                      tener en cuenta sus disposiciones sobre conflictos de
                      leyes.
                    </p>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-700 text-lg mb-3">
                      Jurisdicción
                    </h3>
                    <p className="text-gray-600">
                      Cualquier disputa relacionada con estos términos será
                      sometida a la jurisdicción exclusiva de los tribunales
                      competentes de la Ciudad de México, México.
                    </p>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-gray-300">
                  <h3 className="font-bold text-gray-700 text-lg mb-3">
                    Información de Contacto
                  </h3>
                  <p className="text-gray-600">
                    Para cualquier consulta relacionada con estos Términos de
                    Servicio, por favor contacte a:
                  </p>
                  <div className="mt-3 space-y-2">
                    <p className="text-gray-700">
                      <strong>Packsys S.A. de C.V.</strong>
                    </p>
                    <p className="text-gray-600">Email: legal@packsys.com</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Botón de Aceptación */}
            <div className="mt-12 pt-8 border-t border-gray-200 text-center animate-fade-in animation-delay-2400">
              <p className="text-gray-600 mb-6">
                Al utilizar nuestro sistema de Tracking de Pedidos, usted
                confirma que ha leído, entendido y aceptado estos Términos de
                Servicio en su totalidad.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/tracking"
                  className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-packsys-orange to-orange-500 text-white px-8 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 transform"
                >
                  <span>← Volver al Tracking</span>
                </a>
                <a
                  href="/servicio-al-cliente"
                  className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white px-8 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 transform"
                >
                  <span>📞 Contactar Soporte</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TerminosDeServicio;
